import { Image } from '@storybook';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';

import { ROUTES } from 'routes';

import { API_URL } from 'constant';
import { loginState } from 'global-stores';
import { useEvaultAddon, useNetwork } from 'hooks';
import { PERMISSION_SCREEN, useUserRoles } from 'views';
import {
	Confirm,
	PageHead,
	Pager,
	SearchBox,
	StatusChip,
	Switch,
	Tabs,
} from 'views/design-system/components';
import { fmtDate } from 'views/design-system/format';
import { useSv2Theme } from 'views/design-system/use-sv2-theme';

import { BILLING_PERMISSION_TEXT } from '../component/constant';
import { CancelSubscription } from '../component/subscriptions/components/cancel-subscription';
import { PaymentDrawer } from './payment-drawer';
/* The address editor and the modal that hosts it, reused whole. `AddPaymentMethod`
   is the step machine that owns the Stripe/address forms; `UpdateBiilingAddress`
   is the button that opens it on the address step with the current values
   prefilled. Neither is reimplemented here — a second address form would be a
   second place to get the billing record wrong. */
import { AddPaymentMethod } from '../component/upgrade';
import { UpdateBiilingAddress } from '../component/billing-info/components';
import { useBillingDeck } from './use-billing-deck';
import {
	ActivePaymentType,
	AddCardModalState,
	PaymentNavigationState,
} from '../stores';
/* THE SUBSCRIBE LAUNCHER (spec 18 §7a). The legacy SubscriptionsBox opened this
   same flow from its own Subscribe button; the deck that replaced it never did,
   so after verification a business landed here with nothing to click — the
   only other way in (`/completed` → welcome popup) is routed for PENDING
   businesses only, and a verified one is UNDER_REVIEW. */
import { UserIdentityFlow } from 'views/user-identity-flow';
import { useDashboradhook, usePaymentCard } from 'views/routes-children';
/* `bandLabel` and `hasInternationalSpread` belonged to the old
   `/business-prices` rate card. The flow builder's catalogue prices one figure
   per action — no volume bands, no domestic/international split — so both are
   gone from this page rather than left importing dead helpers. */
import {
	cardBrandImage,
	formatBillingAddress,
	money,
	unitMoney,
	IRate,
	planCta,
} from './derive';

import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import './billing-deck.scss';

/**
 * BILLING — redesigned surface. Design of record: `_design/specs/18-billing-redesign.md`.
 *
 * The old page stacked eight equal-weight blocks and made the reader parse all
 * of them. This one answers the four questions an operator actually arrives
 * with, in order: what am I on, what have I spent, what do I owe, is anything
 * wrong.
 *
 * THE PAGE'S ORGANISING FACT is that Simplici bills two different ways and the
 * old page never said so. There are three RECURRING subscriptions (Onboarding
 * $19.99, Simplici Sign $4.99, Simplici Text $19.99) and twenty-six PER-USE
 * rates (from $0.05 a text to $25 a wire). One cannot be derived from the
 * other, a customer needs both to predict a bill, and the old page showed a
 * single "$19.99/month" as though that were the whole story. Both are first
 * class here.
 *
 * The old `billing.tsx` is untouched and still exported, the way `sessions.tsx`
 * and `simplici-sign-dashboard/` were left when their decks landed.
 */
const PAGE = 10;

const fmtWhen = (iso?: string) => {
	if (!iso) return '—';
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return '—';
	return d.toLocaleString(undefined, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});
};

/**
 * Payment status → the system's own verdict vocabulary (`ok/rev/rej/strt/ns`),
 * so a paid invoice draws exactly like a passed session everywhere else.
 *
 * An UNRECOGNISED status resolves to `rev`, never to `ok`. Stripe's vocabulary
 * is wider than what has been seen locally (`open`, `void`, `uncollectible`),
 * and defaulting an unknown to "fine" is how a genuinely unpaid invoice would
 * render green.
 */
/**
 * One word per state, whatever the source called it.
 *
 * Stripe's vocabulary for "issued but not settled" is `open`; ours is `unpaid`.
 * The Invoices tab merges both sources, so without this the same state appears
 * under two different labels in adjacent rows — which reads as two different
 * things. Normalised for DISPLAY only; the underlying value is untouched.
 */
const payLabel = (status?: string): string => {
	const s = String(status ?? '').toLowerCase();
	if (s === 'open') return 'unpaid';
	return s || 'unknown';
};

/**
 * WHICH INVOICES WE CAN CHARGE OURSELVES.
 *
 * A Stripe-originated invoice carries `hosted_invoice_url` and is paid at
 * Stripe's hosted page — Stripe owns the payment, the SCA challenge and the
 * receipt. Ours have no such URL: they are charged against the default method
 * on file. Only the latter can be batch-charged, which is why only they get a
 * checkbox and enter the footer sum.
 */
const isStripe = (i: any) => !!i?.hosted_invoice_url;
const isPayable = (i: any) => i?.status !== 'paid' && !i?.hosted_invoice_url;

/** "Aug 1 – 31, 2026" from the row's period, or an em-dash. Never faked from
 *  `created`: the issue date is not the period, and guessing one from the other
 *  puts a wrong month on a document a customer reconciles against. */
const fmtPeriod = (i: any): string => {
	const s = i?.period;
	if (typeof s === 'string' && /^\d{4}-\d{2}$/.test(s)) {
		const [y, m] = s.split('-').map(Number) as [number, number];
		const d = new Date(Date.UTC(y, m - 1, 1));
		const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
		return `${d.toLocaleDateString(undefined, {month: 'short', timeZone: 'UTC'})} 1 – ${last}, ${y}`;
	}
	return '—';
};

const payVerdict = (status?: string): 'ok' | 'rej' | 'rev' => {
	const s = String(status ?? '').toLowerCase();
	if (['paid', 'succeeded', 'complete', 'completed'].includes(s)) return 'ok';
	if (['failed', 'uncollectible', 'past_due', 'overdue', 'void'].includes(s))
		return 'rej';
	return 'rev';
};

export const BillingDeck = () => {
	useSv2Theme();
	const { remove: removeCardReq } = useNetwork();
	const { post: postDefault } = useNetwork();
	const { get: getInvoicePdf } = useNetwork({ updateState: false });
	const { post: postPay } = useNetwork();
	const { checkUserWritePermission } = useUserRoles();
	/* Same gate the old billing panel used — reading what is on file is not
	   privileged (it is already in the rail); changing it is. */
	const canWrite = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.Billing),
		[checkUserWritePermission]
	);

	const user = useRecoilValue(loginState) as any;
	const {
		loading,
		refresh: load,
		cycle,
		daysToBilling,
		monthToDate,
		monthly,
		meteredTotal,
		dvRates,
		held,
		services,
		rates,
		usage,
		invoices,
		transactions,
		subAccounts,
		defaultCard,
		cardExpired,
		wire,
		cards,
		banks,
		hasAnyMethod,
		defaultMethod,
		defaultBankId,
		defaultAccountId,
		defaultIsDesignated,
		refreshPayments,
		companyName,
		parentBusinessId,
		account,
	} = useBillingDeck();

	/* Subscribe / upgrade — the one flow that creates a plan. Same choreography
	   the legacy launcher used: the review step prices from
	   `SubscriptionPriceState` and the method picker reads what is on file, so
	   both are fetched before the dialog opens; the deck reloads on close (via
	   `load`, destructured just above) so the new plan, invoice and transaction
	   appear without a page refresh. Declared AFTER `load` so the production
	   ESLint build (no-use-before-define) is happy. */
	const [subscribeOpen, setSubscribeOpen] = useState(false);
	const { getSubscriptionPrice } = useDashboradhook();
	const { getPaymentCardsApi } = usePaymentCard();
	const openSubscribe = useCallback(() => {
		getSubscriptionPrice();
		getPaymentCardsApi();
		setSubscribeOpen(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	const closeSubscribe = useCallback(() => {
		setSubscribeOpen(false);
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/* THE eVAULT ADD-ON (spec 18 §7f, spec 25 §8e; toggle spec 2026-09-09).
	   `useEvaultAddon` owns the whole contract — it fetches `GET /evault-addons`
	   once on its own mount, which IS the deck's mount; `purchase()`/`cancel()`
	   POST/DELETE and update `status` from the response. Nothing here re-derives
	   a price, a period or a per-document rate: every figure below comes off
	   `status`, so the row cannot drift from what the backend will actually
	   charge or invoice. */
	const {
		status: addon,
		isLoaded: addonLoaded,
		isPurchasing: addonBusy,
		purchase: enableAddon,
		cancel: disableAddon,
	} = useEvaultAddon();
	/* Which dialog is open: switching on, switching off, or neither. */
	const [addonConfirm, setAddonConfirm] = useState<'on' | 'off' | null>(null);

	const addonBlocked = !canWrite
		? BILLING_PERMISSION_TEXT.Subscribe
		: addon && !addon.available
		? 'Subscribe to a plan first — the add-on attaches to your existing subscription.'
		: '';

	const confirmAddon = useCallback(
		async () => {
			if (!addonConfirm) return;
			const ok = addonConfirm === 'on' ? await enableAddon() : await disableAddon();
			if (ok) {
				setAddonConfirm(null);
				/* `load` is this file's local name for the hook's `refresh` —
				   see the destructure above (`refresh: load`); every other
				   caller in this file already uses `load()`. */
				load();
			}
		},
		[addonConfirm, enableAddon, disableAddon, load]
	);

	/* `/billing#billing-evault-addon` — the eVault upsell lands here. */
	useEffect(() => {
		if (window.location.hash !== '#billing-evault-addon' || !addonLoaded) return;
		document.getElementById('billing-evault-addon')?.scrollIntoView({block: 'center'});
	}, [addonLoaded]);

	const [tab, setTab] = useState<
		'invoices' | 'transactions' | 'rates' | 'datavalidation'
	>('invoices');
	/* Which country's services are expanded. A country is one row until asked —
	   45 countries times up to 15 services each is 113 rows nobody wants at
	   once. */
	/* `openCountry` is gone with the expandable country rows — a country now has
	   exactly one service, so there is nothing to expand into. */
	/* THE RATE CARD IS THE FIRST-VISIT CONTENT.
	   A fresh business has no invoices and no transactions, so defaulting to
	   Invoices shows it a column header floating over nothing — the emptiest
	   possible first impression of a billing page. The rate card has 26 real
	   priced rows and answers the question a new customer actually has ("what
	   will this cost me"). One shot, after load: the moment an invoice exists
	   the default reverts to Invoices on the next visit. */
	const [payOpen, setPayOpen] = useState(false);
	/*
	 * THE ADD-PAYMENT WIZARD LIVES IN THE DRAWER NOW.
	 *
	 * `AddCardModalState.isOpen` is the wizard's own "I am showing" flag — the
	 * legacy page's modal read it, `UpdateBiilingAddress` sets it, and every
	 * step's close/Back resets it through `handleClose`. The drawer reads the
	 * same flag to swap its list for the form, so nothing here re-implements
	 * the wizard's lifecycle: the deck only opens it, and resets it when the
	 * drawer is dismissed mid-step (otherwise the next Manage lands on a
	 * half-filled Stripe form).
	 */
	const addCardModal = useRecoilValue(AddCardModalState);
	const setAddCardModal = useSetRecoilState(AddCardModalState);
	const resetAddCardModal = useResetRecoilState(AddCardModalState);
	const resetWizardStep = useResetRecoilState(PaymentNavigationState);
	const resetWizardRail = useResetRecoilState(ActivePaymentType);
	const closeDrawer = useCallback(() => {
		setPayOpen(false);
		resetAddCardModal();
		resetWizardStep();
		resetWizardRail();
	}, [resetAddCardModal, resetWizardStep, resetWizardRail]);
	const [defaulted, setDefaulted] = useState(false);
	useEffect(() => {
		if (loading || defaulted) return;
		setDefaulted(true);
		if (!invoices.length && !transactions.length && rates.length) setTab('rates');
	}, [loading, defaulted, invoices.length, transactions.length, rates.length]);
	const [page, setPage] = useState(1);
	const [q, setQ] = useState('');
	/* Selection lives on the deck, not the table, so it survives paging — the
	   running sum is across everything picked, not just this page. */
	const [sel, setSel] = useState<Set<string>>(new Set());
	const [paying, setPaying] = useState<string[] | null>(null);
	const [pendingDefault, setPendingDefault] = useState<any>(null);
	const [payBusy, setPayBusy] = useState(false);
	/* Written by the pay run, read by nothing yet — the count is kept so the
	   "n of m failed" line has a source when it lands. */
	const [, setPayFailed] = useState(0);
	const allRef = useRef<HTMLInputElement | null>(null);

	

	const activePlan = useMemo(() => held.find(s => s.active && s.key !== 'evault'), [held]);
	/* SIM-52: the launcher outlives an INDIVIDUAL plan — see `planCta`. Keyed on
	   the account's own type (from `/users`), not on what is held: an entity
	   that holds only the individual plan has already upgraded and needs
	   "Subscribe", not another upgrade. */
	const planCtaLabel = useMemo(
		() => planCta(activePlan?.key, account?.business?.accountType),
		[activePlan, account]
	);
	const heldAddons = useMemo(() => held.filter(s => s.active && s.key === 'evault'), [held]);
	const billingAddress = useMemo(
		() => formatBillingAddress(account?.business?.billingAddress),
		[account]
	);

	const dvCountries = useMemo(() => dvRates?.countries ?? [], [dvRates]);

	/* ── invoice selection, sums and actions ──────────────────────────── */
	/* Derived from `invoices`, not `paged`: `paged` is declared further down, and
	   select-all should mean "every unpaid invoice I can charge", not "the ones
	   that happen to be on this page" — the sum already spans pages. */
	const payableOnPage = useMemo(
		() => (invoices as any[]).filter(isPayable).map(i => i.id),
		[invoices]
	);
	const allChecked =
		payableOnPage.length > 0 && payableOnPage.every(id => sel.has(id));
	/* Indeterminate is a DOM property, not an attribute — React cannot set it
	   through JSX, so it goes on via the ref. */
	useEffect(() => {
		if (!allRef.current) return;
		const some = payableOnPage.some(id => sel.has(id));
		allRef.current.indeterminate = some && !allChecked;
	}, [payableOnPage, sel, allChecked]);

	const toggleOne = useCallback((id: string) => {
		setSel(prev => {
			const next = new Set(prev);
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});
	}, []);
	const toggleAll = useCallback(() => {
		setSel(prev => {
			const next = new Set(prev);
			if (payableOnPage.every(id => next.has(id))) {
				payableOnPage.forEach(id => next.delete(id));
			} else {
				payableOnPage.forEach(id => next.add(id));
			}
			return next;
		});
	}, [payableOnPage]);

	const invoiceById = useMemo(() => {
		const m = new Map<string, any>();
		(invoices as any[]).forEach(i => m.set(i.id, i));
		return m;
	}, [invoices]);
	const selSum = useMemo(
		() =>
			Array.from(sel).reduce(
				(t, id) => t + Number(invoiceById.get(id)?.total ?? 0) / 100,
				0
			),
		[sel, invoiceById]
	);
	const openCount = useMemo(
		() => (invoices as any[]).filter(i => i.status !== 'paid').length,
		[invoices]
	);
	const openSum = useMemo(
		() =>
			(invoices as any[])
				.filter(i => i.status !== 'paid')
				.reduce((t, i) => t + Number(i.total ?? 0) / 100, 0),
		[invoices]
	);

	/* A selection that outlives its invoices is a sum of things that no longer
	   exist — prune whatever the refetch removed. */
	useEffect(() => {
		setSel(prev => {
			const next = new Set(Array.from(prev).filter(id => invoiceById.has(id)));
			return next.size === prev.size ? prev : next;
		});
	}, [invoiceById]);

	/**
	 * Charge the selected invoices.
	 *
	 * The confirm step is not ceremony: paying is the one IRREVERSIBLE action on
	 * this page (changing a default is undone in a click; a charge is not). The
	 * risk being guarded is mis-SCOPE, not mis-intent — picking three invoices
	 * and paying six — so the modal itemises exactly what will be charged and
	 * puts the amount on the button. A typed confirmation would guard intent,
	 * which was never in doubt, and is hostile for paying your own bill.
	 */
	const confirmPay = useCallback(async () => {
		if (!paying?.length) return;
		setPayBusy(true);
		try {
			const resp: any = await postPay('/monthly-invoices', { invoiceIds: paying });
			const results = Array.isArray(resp?.data) ? resp.data : resp?.data ?? resp;
			const failed = (Array.isArray(results) ? results : []).filter(
				(r: any) => r?.status === 'failed'
			);
			setPayFailed(failed.length);
			setPaying(null);
			setSel(new Set());
			await load();
		} finally {
			setPayBusy(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paying, postPay, load]);

	/**
	 * WHAT IS ACTUALLY GOING TO BE CHARGED, IN WORDS.
	 *
	 * The confirm modal said "Charging your default method — your default
	 * payment method", which is both redundant and useless: it names nothing.
	 * That happened because only the CARD branch was wired, and this account's
	 * default is a bank — so the fallback fired on the exact case a customer
	 * most needs identified.
	 *
	 * Before an irreversible charge the instrument has to be named, not
	 * described by its role.
	 */
	const defaultMethodLabel = useMemo(() => {
		if (defaultMethod === 'card' && defaultCard) {
			const exp =
				defaultCard.exp_month && defaultCard.exp_year
					? ` · exp ${String(defaultCard.exp_month).padStart(2, '0')}/${String(
							defaultCard.exp_year
					  ).slice(-2)}`
					: '';
			return `${defaultCard.brand ?? 'Card'} ••${defaultCard.last4 ?? '????'}${exp}`;
		}
		if (defaultMethod === 'ach') {
			const bank = (banks as any[]).find(b => b._id === defaultBankId) ?? (banks as any[])[0];
			const acct =
				(bank?.accounts ?? []).find((a: any) => a.accountId === defaultAccountId) ??
				bank?.accounts?.[0];
			if (bank) {
				return `${(bank.bankName ?? 'Bank').toUpperCase()} ••${acct?.mask ?? '????'}${
					acct?.name ? ` · ${acct.name}` : ''
				}`;
			}
			return 'your linked bank account';
		}
		if (defaultMethod === 'wire') return 'wire transfer';
		return null;
	}, [defaultMethod, defaultCard, banks, defaultBankId, defaultAccountId]);

	const downloadPdf = useCallback(async (i: any) => {
		/* Stripe hands back a signed URL that opens directly. Ours is a route on
		   our own API that needs the bearer token, so it cannot just be opened in
		   a tab — it is fetched, then turned into a Blob for the download. */
		if (String(i.invoice_pdf ?? '').startsWith('http')) {
			window.open(i.invoice_pdf, '_blank', 'noopener');
			return;
		}
		try {
			const resp: any = await getInvoicePdf(`/monthly-invoices/${i.id}`);
			const payload = Array.isArray(resp?.data) ? resp.data[0] : resp?.data ?? resp;
			if (!payload?.pdf) throw new Error('no document');
			/* base64 -> bytes. `atob` gives a binary string; each char code is one
			   byte, and skipping this step produces a corrupt PDF that opens to a
			   blank page rather than failing loudly. */
			const bin = atob(payload.pdf);
			const bytes = new Uint8Array(bin.length);
			for (let n = 0; n < bin.length; n += 1) bytes[n] = bin.charCodeAt(n);
			const url = URL.createObjectURL(
				new Blob([bytes], { type: 'application/pdf' })
			);
			const a = document.createElement('a');
			a.href = url;
			a.download = payload.fileName ?? `${i.number ?? 'invoice'}.pdf`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			URL.revokeObjectURL(url);
		} catch {
			/* surfaced by useNetwork's toast */
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/* One filter for whichever table is showing — the tabs share a control
	   strip, so they have to share its state too. */
	const rows = useMemo(() => {
		const needle = q.trim().toLowerCase();
		const match = (hay: string) => !needle || hay.toLowerCase().includes(needle);
		if (tab === 'invoices')
			return invoices.filter(i => match(`${i.number ?? ''} ${i.status ?? ''}`));
		if (tab === 'transactions')
			return transactions.filter(t =>
				match(`${t.transactionId ?? ''} ${t.type ?? ''} ${t.status ?? ''}`)
			);
		if (tab === 'rates')
			return rates.filter(r => match(`${r.label} ${r.service}`));
		/* Country name OR any service name — someone hunting "Aadhaar" should
		   find India without knowing it is Indian. */
		return (dvCountries as any[]).filter(c =>
			match(
				`${c.country} ${(c.services ?? [])
					.map((sv: any) => sv.service)
					.join(' ')}`
			)
		);
	}, [tab, q, invoices, transactions, rates, dvCountries]);

	/* THE RATE CARD IS NOT PAGED. It is a reference list of about ten lines —
	   paging it hides half the answer behind a control and leaves a mostly
	   empty panel. The money tables stay paged; they grow without limit. */
	const paged = useMemo(
		() =>
			tab === 'rates' || tab === 'datavalidation'
				? rows
				: rows.slice((page - 1) * PAGE, page * PAGE),
		[rows, page, tab]
	);
	const totalPages =
		tab === 'rates' || tab === 'datavalidation'
			? 1
			: Math.max(1, Math.ceil(rows.length / PAGE));

	/* Whole days elapsed of whole days in the period, both from the server's
	   own timestamps — never a client-side "30 days from now". */
	/*
	 * ADD / REPLACE goes through the EXISTING wizard, which owns the Stripe
	 * token exchange. Reimplementing card capture here would mean a second place
	 * that can mishandle a PAN, which is never worth it. The wizard mounts inside
	 * the drawer, so opening it is: show the drawer, raise its flag.
	 */
	const openAddPayment = useCallback(() => {
		setAddCardModal({ isOpen: true, isEdit: false });
		setPayOpen(true);
	}, [setAddCardModal]);

	/* THE WIZARD'S PROCESSING STEP REFETCHES INTO RECOIL, NOT INTO THIS HOOK —
	   so the drawer list and the rail summary stayed stale after a card was
	   added until a full reload. One refetch when the wizard closes, whatever
	   closed it; a GET on a plain Back is the price of never showing a list
	   that is missing the card just saved. */
	const wizardWasOpen = useRef(false);
	useEffect(() => {
		if (wizardWasOpen.current && !addCardModal.isOpen) refreshPayments();
		wizardWasOpen.current = addCardModal.isOpen;
	}, [addCardModal.isOpen, refreshPayments]);

	/**
	 * ONE HANDLER FOR BOTH RAILS.
	 *
	 * This used to be `PATCH /business-cards/:id {default:true}`, which is only
	 * half a designation: it sets Stripe's `defaultSource` and never touches
	 * `business.paymentMethod.default`. So on an account whose default is ACH,
	 * clicking "make this card default" appeared to do NOTHING — `resolveDefault`
	 * gives the explicit designation priority, so the UI kept showing the bank.
	 *
	 * `POST /payment-methods` already accepts `method: 'card' | 'ach' | 'wire'`
	 * and does both halves, so everything goes through it and the card-only
	 * PATCH leaves the default path entirely. No backend change needed.
	 */
	const setDefault = useCallback(
		async (selection: any) => {
			const payload =
				selection.kind === 'card'
					? { method: 'card', data: { id: selection.id } }
					: {
							method: 'ach',
							data: {
								id: selection.bankId,
								accountId: selection.accountId,
								/* The validator only accepts CHECKING|SAVINGS|BUSINESS —
								   a Plaid subtype outside that set 400s, so the drawer
								   hides the control rather than offering a call that
								   cannot succeed. */
								accountType: String(selection.accountType ?? '').toUpperCase(),
							},
					  };
			/* Optimistic: the chip moves immediately and reverts if the call fails,
			   because a designation that takes a round-trip to appear reads as a
			   dead button. */
			setPendingDefault(selection);
			try {
				await postDefault(API_URL.Payment_Methods, payload);
				await refreshPayments();
			} finally {
				setPendingDefault(null);
			}
		},
		[postDefault, refreshPayments]
	);

	const removeCard = useCallback(
		async (card: any) => {
			await removeCardReq(`${API_URL.BUSINESS_CARD}/${card.id}`);
			await refreshPayments();
		},
		[removeCardReq, refreshPayments]
	);

	const cycleProgress = useMemo(() => {
		if (!cycle.start || !cycle.end) return null;
		const span = cycle.end - cycle.start;
		if (span <= 0) return null;
		const days = Math.max(1, Math.round(span / 86_400_000));
		const elapsed = Math.min(
			Math.max(0, Date.now() - cycle.start),
			span
		);
		return {
			day: Math.min(days, Math.floor(elapsed / 86_400_000) + 1),
			days,
			pct: Math.min(100, Math.round((elapsed / span) * 100)),
		};
	}, [cycle.start, cycle.end]);

	const failedCount = useMemo(
		() => transactions.filter(t => payVerdict(t.status) === 'rej').length,
		[transactions]
	);

	return (
		/* `sv2-embed-scope` is not decoration. This deck mounts LEGACY components
		   whole — AddPaymentMethod, AddPaymentCard, BillingInfoForm,
		   UpdateBiilingAddress — and those read the panel's own `--color-*`
		   names, which tokens.scss only remaps inside `.sidebar-container` and
		   `.sv2-embed-scope`. Without it they render their light palette on a dark
		   page: white input fields, unreadable labels. The roles and general
		   settings decks add it for exactly this reason. */
		<div className="sv2-scope sv2 sv2-embed-scope billing-deck">
			<PageHead
				title="Billing"
				sub={
					loading
						? companyName || 'Your account'
						: `${companyName || 'Your account'} · cycle ${fmtDate(cycle.start)} – ${fmtDate(
								cycle.end
						  )}`
				}
			/>

			{/* Conditional alerts only. A band that is always present is furniture,
			    and furniture is not read. */}
			{(cardExpired || !hasAnyMethod) && (
				<div className="balert">
					{/* The band used to state the problem and offer nothing to do
					    about it, which is the worst pairing available. `.errbar` is
					    already flex, so the action needs no primitive override. */}
					<div className="errbar">
						{cardExpired
							? 'Your card has expired. Payments will fail until it is replaced.'
							: 'No payment method on file. Add one before your next billing date.'}
						{canWrite && (
							<button
								type="button"
								className="errbar__act"
								onClick={openAddPayment}
							>
								{cardExpired ? 'Replace card' : 'Add a payment method'}
							</button>
						)}
					</div>
				</div>
			)}

			{/* ── THE LEDGER: one panel, one lead, three legs, one meter ──
			    Four identical bordered tiles with four identical 22px figures was
			    the single biggest reason this page read like a spreadsheet —
			    nothing led, so everything had to be read. This is the Ops Deck's
			    own `.rangebar`/`.rb-leg` language, which the system already uses
			    for exactly this problem. Same four facts, same order; only the
			    weight differs now. */}
			<div className="panel">
				<div className="bledger">
					<div className="bledger__lead">
						<div className="l">Month to date</div>
						{/* ZERO IS QUIET, NOT SMALL. Size never changes between $0.00
						    and $1,284.40 — only colour — so the layout does not jump
						    the moment the first charge lands. */}
						<div className={`n${monthToDate ? '' : ' zero'}`}>
							<span className="cur">$</span>
							{money(monthToDate)}
						</div>
						<div className="d">
							{usage.length
								? `${usage.length} metered item${usage.length === 1 ? '' : 's'}`
								: monthly
								? 'subscription accrued this cycle'
								: 'nothing billed this cycle'}
						</div>
					</div>

					<div className="bledger__leg">
						<div className="l">Next billing</div>
						<div className="n">{fmtDate(cycle.billingDate ?? cycle.end)}</div>
						<div className={`d${daysToBilling !== null && daysToBilling <= 7 ? ' warn' : ''}`}>
							{daysToBilling === null
								? 'no active plan'
								: `in ${daysToBilling} day${daysToBilling === 1 ? '' : 's'}`}
						</div>
					</div>

					<div className="bledger__leg name">
						<div className="l">Plan</div>
						<div className="n">{activePlan?.label ?? 'No plan'}</div>
						<div className="d">
							{/* The plan's OWN price — `monthly` sums every active held
							    service, add-ons included, and belongs to month-to-date,
							    not to a line that names one specific plan. */}
							{activePlan ? `$${money(activePlan.monthly)} / mo` : 'not subscribed'}
						</div>
					</div>

					{/* The failing leg takes the treatment; the container never
					    recolours, so the other three facts stay truthful. */}
					<div
						className={`bledger__leg${cardExpired || !hasAnyMethod ? ' bad' : ''}`}
					>
						<div className="l">Payment method</div>
						<div className="n">
							{defaultCard
								? `${(defaultCard.brand ?? '').toUpperCase()} ····${defaultCard.last4}`
								: wire?.accountNumber
								? 'Wire'
								: '—'}
						</div>
						<div className="d">
							{!hasAnyMethod
								? 'none — add one'
								: cardExpired
								? 'EXPIRED'
								: defaultCard
								? `exp ${String(defaultCard.exp_month).padStart(2, '0')}/${String(
										defaultCard.exp_year
								  ).slice(-2)}`
								: wire?.bankName ?? 'on file'}
						</div>
					</div>

				</div>

				{/* The cycle meter spans the ledger's full width, below the grid —
				    it is the context every figure above depends on, and it draws
				    the same fact the "in 32 days" leg states in prose. Neon fills
				    the meter and never colours text, per the Ops Deck rule. */}
				{cycleProgress !== null && (
					<div className="bledger-cycle">
						<div className="track">
							<div
								className="fill"
								style={{ width: `${cycleProgress.pct}%` }}
							/>
						</div>
						<span className="mn">
							day {cycleProgress.day} of {cycleProgress.days}
						</span>
					</div>
				)}
			</div>

			<div className="bgrid">
				{/* ── LEFT: what you're on, and what you've burned ── */}
				<div className="panel">
					<div className="ph">
						<div className="t">Plan &amp; usage</div>
					</div>

					<div className="plan-head">
						<span className="plan-head__name">
							{activePlan?.label ?? 'No active subscription'}
						</span>
						<span className="plan-head__price">
							{/* The plan's own price, not `monthly` (plan + add-ons) —
							    same reason as the ledger's PLAN leg above. */}
							{activePlan ? `$${money(activePlan.monthly)} / mo` : '—'}
						</span>
						{activePlan && <StatusChip verdict="ok">Active</StatusChip>}
						{planCtaLabel && !loading && (
							<button
								type="button"
								className="cta plan-head__cta"
								onClick={openSubscribe}
								disabled={!canWrite}
								title={!canWrite ? BILLING_PERMISSION_TEXT.Subscribe : undefined}
							>
								{planCtaLabel}
							</button>
						)}
					</div>

					{/* WHAT THE CATALOGUE PRICES — not what is held.
					    `/subscriptions` answers by ACCOUNT TYPE (`entity`) and the
					    catalogue by SERVICE (`onboardingService`); there is no join
					    between them, so this row cannot claim which are active
					    without inventing one. It lists the published monthly prices
					    and says so. The held plan is the line above. */}
					<div className="plan-svc">
						<span className="plan-svc__lbl">Subscription services</span>
						{services.map(sv => (
							<span key={sv.key} className="plan-svc__item">
								{sv.label}
								<i>${unitMoney(sv.monthly)}/mo</i>
							</span>
						))}
						{/* A held add-on is real regardless of whether the catalogue
						    answered — it shows even on an empty `services` response. */}
						{heldAddons.map(sv => (
							<span key={sv.key} className="plan-svc__item plan-svc__item--held">
								{sv.label}
								<i>${unitMoney(sv.monthly)}/mo</i>
								<span className="vch ok">ADDED</span>
							</span>
						))}
						{!services.length && !heldAddons.length && (
							<span className="plan-svc__none">
								No subscription services published
							</span>
						)}
					</div>

					{/* ── metered usage: what has actually been consumed ── */}
					<div className="usage-tbl">
						<div className="usage-tbl__head">
							Metered usage this cycle
							<span className={meteredTotal ? '' : 'zero'}>
								${money(meteredTotal)}
							</span>
						</div>
						{usage.length ? (
							<div className="tscroll">
								<table className="stbl">
									<thead>
										<tr>
											<th>Product</th>
											<th className="num">Qty</th>
											<th className="num">Unit (USD)</th>
											<th className="num">Total (USD)</th>
										</tr>
									</thead>
									<tbody>
										{usage.map(l => (
											<tr key={l.key ?? l.name}>
												<td>{l.name ?? l.key}</td>
												<td className="num">{l.quantity}</td>
												{/* Derived, and shown as derived — the server sends
												    only the line total. */}
												<td className="num">
													{l.unit === null ? '—' : unitMoney(l.unit)}
												</td>
												<td className="num">{money(l.total)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						) : (
							<div className="bwell">
								<div className="t">Per-use charges appear here as they happen</div>
								<div className="d">
									KYC, AML, texts and wires are billed per event, not monthly.
								</div>
							</div>
						)}
					</div>

					{/* ── THE ADD-ON, AT THE PANEL'S FOOT ──
					    eVault attaches to the subscription above it, so it belongs
					    under the plan rather than in a section of its own — a
					    fourth panel for one purchasable line was how the old page
					    ended up with eight equal blocks.

					    RENDERED ONLY ONCE THE STATUS CALL HAS RESOLVED. `status`
					    is null both before the request lands and after it fails,
					    and the two must look the same: flashing the switch at
					    somebody who already owns it — or offering a toggle this
					    page cannot price — is worse than showing nothing. */}
					{addonLoaded && addon && (
						<div
							id="billing-evault-addon"
							className="addon"
							title="Securely store the authoritative original of every signed document in an encrypted vault with a cryptographic seal and tamper-evident audit trail."
						>
							<span className="addon__tile">◈</span>
							<span className="addon__name">eVault</span>
							<span className="vch strt">NEW</span>
							{addon.active && !addon.endsAt && <span className="vch ok">ADDED</span>}
							{addon.endsAt && (
								<span className="vch rev">ENDS {fmtDate(addon.endsAt)}</span>
							)}

							{addon.active ? (
								<>
									<span className="addon__usage">
										${money(addon.monthlyCharge)} this cycle ·{' '}
										{addon.usage.quantity}{' '}
										{addon.usage.quantity === 1 ? 'document' : 'documents'} × $
										{money(addon.transferPrice)} ={' '}
										<b>${money(addon.usage.amount)}</b>
									</span>
									{/* THE LIST ITSELF IS NOT ON THIS PAGE ANY MORE. It has its
									    own eVault page; this row keeps the figure the invoice
									    uses and one link to what produced it, rather than a tab
									    about vault activity sitting among tabs about payments. */}
									<Link className="bsec__manage" to={ROUTES.EVAULT_TRANSFERS}>
										View transfers
									</Link>
								</>
							) : (
								<span className="addon__price">${money(addon.price)} / month</span>
							)}

							<Switch
								checked={addon.active && !addon.endsAt}
								disabled={!!addonBlocked || addonBusy}
								label="eVault add-on"
								title={addonBlocked || (addon.active && !addon.endsAt ? 'Switch eVault off at the end of this cycle' : 'Add eVault to your plan')}
								onChange={next => setAddonConfirm(next ? 'on' : 'off')}
							/>
						</div>
					)}
					{/* CANCEL — the plan panel's last row, and nothing more than the
					    button. The dashed red zone with its consequence copy read as a
					    prompt to leave; the confirmation modal already spells out what
					    cancelling does, so the row says it once, there. The existing
					    step machine (`cancel-subscription`) is reused whole: it owns
					    the dues lookup, the confirmation steps and the DELETE. */}
					{activePlan && (
						<div className="plan-foot">
							<CancelSubscription />
						</div>
					)}
				</div>

				{/* ── RIGHT: account, payment, verification ── */}
				<div className="panel">
					<div className="ph">
						<div className="t">Account &amp; payment</div>
					</div>

					<div className="bsec">
						<div className="bsec__h">
							Payment method
							<button
								type="button"
								className="bsec__manage"
								onClick={() => setPayOpen(true)}
							>
								Manage
							</button>
						</div>
						{defaultCard ? (
							/* The brand mark sits at the RIGHT of the row, so the eye
							   reads the card's identity in one place across every row
							   rather than hunting a logo that moves with the name's
							   length. */
							<div className="bsec__row">
								<div className="bsec__rowmain">
									<div className="bsec__v">
										{(defaultCard.brand ?? '').toUpperCase()} •••• {defaultCard.last4}
									</div>
									<div className={`bsec__sub${cardExpired ? ' bad' : ''}`}>
										exp {String(defaultCard.exp_month).padStart(2, '0')}/
										{String(defaultCard.exp_year).slice(-2)}
										{cardExpired ? ' · expired' : ''}
										{defaultIsDesignated ? ' · default' : ''}
									</div>
								</div>
								<Image
									fileName={cardBrandImage(defaultCard.brand)}
									className="bsec__brand"
								/>
							</div>
						) : wire?.accountNumber ? (
							<>
								<div className="bsec__v">Wire transfer</div>
								<div className="bsec__sub">
									{wire.bankName} · ••••{String(wire.accountNumber).slice(-4)}
								</div>
							</>
						) : (
							<div className="bsec__sub">None saved</div>
						)}
					</div>

					<div className="bsec">
						{/* ONE DOOR. "Manage" above opens everything about how this
						    account pays — cards, banks, wire AND the billing address —
						    because those are one errand, not two. A second action here
						    split a single job across two controls in one panel. */}
						<div className="bsec__h">Billing info</div>
						<div className="kv">
							<span>Business</span>
							<b>{companyName || '—'}</b>
						</div>
						<div className="kv">
							<span>Name</span>
							<b>
								{[
									account?.firstName ?? user?.firstName,
									account?.lastName ?? user?.lastName,
								]
									.filter(Boolean)
									.join(' ') || '—'}
							</b>
						</div>
						<div className="kv">
							<span>Email</span>
							<b>{account?.email ?? user?.email ?? '—'}</b>
						</div>
						{/* The address appears on every invoice, so its absence is worth
						    stating rather than leaving the reader to notice a missing
						    row. `billingAddress` is always present and usually blank,
						    which is why it is formatted rather than truth-tested. */}
						<div className="kv kv--addr">
							<span>Address</span>
							{billingAddress ? (
								<b>{billingAddress}</b>
							) : (
								<b className="kv__none">None saved</b>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* ── the money tables, and the rate card the old page never showed ── */}
			<div className="panel money-panel">
				<div className="ph">
					<div className="t">Invoices, transactions &amp; rates</div>
				</div>
				<div className="money-ctl">
					<Tabs
						tabs={[
							{ key: 'invoices', label: 'Invoices', count: invoices.length },
							{
								key: 'transactions',
								label: 'Transactions',
								count: transactions.length,
							},
							{ key: 'rates', label: 'Rate card', count: rates.length },
							{
								key: 'datavalidation',
								label: 'Global data validation',
								count: dvCountries.length,
							},
						]}
						active={tab}
						onChange={(k: any) => {
							setTab(k);
							setPage(1);
						}}
					/>
					<SearchBox
						value={q}
						onChange={(v: string) => {
							setQ(v);
							setPage(1);
						}}
						placeholder={tab === 'rates' ? 'Search rates' : 'Search'}
					/>
				</div>

				{failedCount > 0 && tab === 'transactions' && (
					<div className="money-panel__warn">
						{failedCount} transaction{failedCount === 1 ? '' : 's'} need attention
					</div>
				)}

				<div className="tscroll">
					{/* NO HEADER OVER NOTHING. Column headings floating above an
					    empty body was the most broken-looking thing on the page —
					    the well below carries the message instead. */}
					{!!rows.length && tab === 'invoices' && (
						<>
						<table className="stbl inv-tbl">
							<thead>
								<tr>
									<th className="ck">
										<input
											type="checkbox"
											ref={allRef}
											checked={allChecked}
											onChange={toggleAll}
											aria-label="Select all payable invoices"
										/>
									</th>
									<th>Invoice</th>
									<th>Billing period</th>
									<th>Issued</th>
									<th>Status</th>
									<th className="num">Amount (USD)</th>
									<th className="act" />
									<th className="sp" />
								</tr>
							</thead>
							<tbody>
								{paged.map((i: any) => (
									<tr key={i.id ?? i.number} className={sel.has(i.id) ? 'sel' : ''}>
										{/* A checkbox means exactly one thing: "this can be
										    batch-charged against your default method". Stripe's
										    own invoices pay at Stripe's hosted page, so they get
										    no checkbox and never enter the sum — the footer
										    total is precisely what the card is charged. */}
										<td className="ck">
											{isPayable(i) && (
												<input
													type="checkbox"
													checked={sel.has(i.id)}
													onChange={() => toggleOne(i.id)}
													aria-label={`Select ${i.number}`}
												/>
											)}
										</td>
										<td className="no">
											<b>{i.number ?? i.id ?? '—'}</b>
											{isStripe(i) && <span className="inv-src">STRIPE</span>}
										</td>
										<td className="per">{fmtPeriod(i)}</td>
										<td className="iss">{i.created ? fmtDate(i.created * 1000) : '—'}</td>
										<td className="st">
											<StatusChip verdict={payVerdict(i.status)}>
												{payLabel(i.status)}
											</StatusChip>
										</td>
										<td className="num amt">{money((i.total ?? 0) / 100)}</td>
										<td className="act">
											<span className="inv-act">
												{i.invoice_pdf && (
													<button
														className="dbtn dbtn--icon"
														type="button"
														title={`Download ${i.number ?? 'invoice'} (PDF)`}
														aria-label={`Download ${i.number ?? 'invoice'} as PDF`}
														onClick={() => downloadPdf(i)}
													>
														<i className="ri-file-pdf-line" aria-hidden="true" />
													</button>
												)}
												{/* Paid rows get no pay affordance — the chip says paid
												    and the PDF is the receipt. A disabled money button
												    on every historical row is noise. */}
												{i.status !== 'paid' &&
													(isStripe(i) ? (
														<button
															className="dbtn pri"
															type="button"
															onClick={() => window.open(i.hosted_invoice_url, '_blank', 'noopener')}
														>
															Pay at Stripe ↗
														</button>
													) : (
														<button className="dbtn pri" type="button" onClick={() => setPaying([i.id])}>
															Pay now
														</button>
													))}
											</span>
										</td>
										<td className="sp" />
									</tr>
								))}
							</tbody>
							{/* IN THE TABLE, NOT UNDER IT.
							    As a sibling <div> the Clear/Pay pair could only be
							    positioned by guessing an offset — and the columns are
							    content-sized, so any guess drifts the moment an invoice
							    number or amount changes width. A `tfoot` row shares the
							    column grid, so the buttons sit exactly beneath the
							    row-level PDF / Pay now buttons by construction. */}
							<tfoot className="inv-foot">
								<tr>
									<td className="ck" />
									<td className="no" colSpan={4}>
										{sel.size === 0 ? (
											<span>
												{rows.length} invoice{rows.length === 1 ? '' : 's'} ·{' '}
												{openCount} unpaid · <b>{money(openSum)}</b> outstanding
											</span>
										) : (
											<span>
												<b>{sel.size}</b> selected · <b>{money(selSum)}</b>
											</span>
										)}
									</td>
									<td className="act">
										{sel.size > 0 && (
											<span className="inv-act">
												<button className="dbtn" type="button" onClick={() => setSel(new Set())}>
													Clear
												</button>
												<button className="paybtn" type="button" onClick={() => setPaying(Array.from(sel))}>
													Pay {money(selSum)}
												</button>
											</span>
										)}
									</td>
									<td className="sp" />
								</tr>
							</tfoot>
						</table>
						</>
					)}

					{!!rows.length && tab === 'transactions' && (
						<table className="stbl">
							<thead>
								<tr>
									<th>Transaction</th>
									<th>When</th>
									<th>Type</th>
									<th className="num">Amount (USD)</th>
									<th>Status</th>
								</tr>
							</thead>
							<tbody>
								{paged.map((t: any) => (
									<tr key={t.transactionId ?? t.paymentId}>
										<td className="mono">{t.transactionId ?? '—'}</td>
										<td>{fmtWhen(t.time)}</td>
										<td>{t.type ?? '—'}</td>
										<td className="num">{money(t.amount)}</td>
										<td>
											<StatusChip verdict={payVerdict(t.status)}>
												{t.status ?? 'unknown'}
											</StatusChip>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					)}

					{/* THE RATE CARD — 26 per-use prices that existed in the data and
					    appeared nowhere on the old page. A customer could not find out
					    what a KYB or a wire costs before being billed for it. */}
					{/* THE RATE CARD IS A LIST, NOT A LEDGER.
					    As a full-width table the price sat at the far right edge with
					    a hand's width of empty row between it and the name it belonged
					    to — the eye had to travel to pair them, on the one table where
					    pairing name-to-price IS the whole content. Two columns of
					    compact rows halves the travel and doubles the density; nothing
					    here needs to line up with the money tables beside it. */}
					{!!rows.length && tab === 'rates' && (
						<div className="rate-grid">
							{(paged as IRate[]).map((r, i, arr) => (
								<Fragment key={r.key}>
									{r.step && (i === 0 || arr[i - 1]?.step !== r.step) && (
										<div className="rate-grid__group">
											{r.stepLabel || r.step}
										</div>
									)}
									<div className="rate-grid__row" title={r.note || undefined}>
										<span className="rate-grid__name">{r.label}</span>
										<span className="rate-grid__price">
											{r.kind === 'included' ? (
												<span className="rate-free">Included</span>
											) : r.kind === 'unavailable' ? (
												<span className="rate-none">—</span>
											) : (
												<>
													<span className="cur">$</span>
													{unitMoney(r.domestic)}
												</>
											)}
										</span>
									</div>
								</Fragment>
							))}
						</div>
					)}
					{/* ── GLOBAL DATA VALIDATION ──
					    One row per country, priced from the vendor rate card at
					    cost + margin (the server does the markup; this client never
					    sees cost). A country expands to its services because the
					    price is a RANGE, not a number — a check only runs the
					    registries the applicant's details unlock, so "from $0.08 ·
					    up to $1.31" is the honest quote and the services explain
					    why. */}
					{!!rows.length && tab === 'datavalidation' && (
						<div className="rate-grid rate-grid--dv">
							{(paged as any[]).map(c => {
									/* ONE SERVICE PER COUNTRY, SO ONE PRICE — no caret, no
									   expansion. A data validation runs exactly one check:
									   Didit's contract is "if you omit [services], exactly one
									   default service runs", and for the two countries where we
									   DO pass an explicit list the seeded row already carries the
									   summed cost of what our own code fires.

									   This used to render a range (`$0.36 – $22.14`) built by
									   summing every service Didit lists for a country. Both ends
									   were fiction: the top added products we never call
									   (criminal screening, vehicle ownership), and the bottom was
									   the cheapest service rather than the one that runs. A
									   customer cannot be quoted a number that cannot occur. */
									const svc = (c.services ?? [])[0];
									return (
										<div className="rate-grid__cgroup" key={c.country}>
											<div className="rate-grid__row rate-grid__country">
												<span className="rate-grid__name">
													{c.country}
													{svc?.service && (
														<i className="rate-grid__meta">{svc.service}</i>
													)}
												</span>
												<span className="rate-grid__price">
													<span className="cur">$</span>
													{unitMoney(c.from)}
												</span>
											</div>
										</div>
									);
								})}

						</div>
					)}

				</div>

				{!rows.length && !loading && (
					<div className="bwell">
						<div className="t">
							{tab === 'invoices'
								? 'No invoices yet'
								: tab === 'transactions'
								? 'No transactions yet'
								: 'No rates published'}
						</div>
						<div className="d">
							{tab === 'invoices'
								? 'The first one arrives after your first billing date.'
								: tab === 'transactions'
								? 'Payments and charges appear here once billing starts.'
								: 'This business has no published price list.'}
						</div>
					</div>
				)}

				{totalPages > 1 && (
					<Pager page={page} pages={totalPages} onChange={setPage} />
				)}
			</div>

			{/* Only for a top-level business — a sub-account has no sub-accounts. */}
			{!parentBusinessId && (
				<div className="panel">
					<div className="ph">
						<div className="t">Sub-accounts</div>
						<div className="r">{subAccounts.length} total</div>
					</div>
					{subAccounts.length ? (
						<div className="tscroll">
							<table className="stbl subacct-tbl">
								<thead>
									<tr>
										<th>Name</th>
										<th>Created</th>
										<th className="num">Users</th>
										<th className="num">To date (USD)</th>
									</tr>
								</thead>
								<tbody>
									{subAccounts.map((a: any) => (
										<tr key={a._id ?? a.id}>
											<td>{a.name ?? '—'}</td>
											<td>{a.createdAt ? fmtDate(new Date(a.createdAt).getTime()) : '—'}</td>
											<td className="num">{a.userCount ?? 0}</td>
											<td className="num">{money(a.totalAmount ?? 0)}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : (
						/* A hollow panel to say "none" is a whole container spent on an
						   absence. One quiet line does the same work. */
						<div className="bfoot-note">
							No sub-accounts — businesses added here bill under this account.
						</div>
					)}
				</div>
			)}

			{/* Mounted only while open: the flow's mount effect picks its landing
			    step from the business's verification state, and it must run fresh
			    each time the button is pressed. */}
			{subscribeOpen && (
				<UserIdentityFlow isOpen handleCloseModal={closeSubscribe} />
			)}

			{/* CONFIRM BEFORE CHARGING. Itemised so the scope is unmistakable, with
			    the exact amount on the button — the failure this guards is paying
			    for more invoices than you meant to. */}
			{!!paying?.length && (
				<div className="paym" role="dialog" aria-modal="true" aria-label="Confirm payment">
					<div className="paym__box">
						<div className="paym__head">Pay {paying.length} invoice{paying.length === 1 ? '' : 's'}</div>
						<ul className="paym__list">
							{paying.map(id => {
								const inv = invoiceById.get(id);
								return (
									<li key={id}>
										<span>{inv?.number ?? id}</span>
										<b>{money((inv?.total ?? 0) / 100)}</b>
									</li>
								);
							})}
						</ul>
						<div className="paym__tot">
							<span>Total charged today</span>
							<b>
								{money(
									paying.reduce(
										(t, id) => t + Number(invoiceById.get(id)?.total ?? 0) / 100,
										0
									)
								)}
							</b>
						</div>
						{/* Never offer a charge that cannot succeed. */}
						{!hasAnyMethod || cardExpired ? (
							<div className="paym__warn">
								{cardExpired
									? 'The default card has expired. Update it before paying.'
									: 'No payment method on file. Add one before paying.'}
							</div>
						) : (
							<div className="paym__meth">
								Charging <b>{defaultMethodLabel ?? 'your default method'}</b>
								{defaultIsDesignated ? '' : ' (used automatically — no default chosen)'}
								{'. '}
								Change it in <b>Account &amp; payment → Manage</b>.
							</div>
						)}
						<div className="paym__act">
							<button className="dbtn" type="button" onClick={() => setPaying(null)}>
								Cancel
							</button>
							<button
								className="paybtn"
								type="button"
								disabled={payBusy || !hasAnyMethod || cardExpired}
								onClick={confirmPay}
							>
								{payBusy
									? 'Charging…'
									: `Pay ${money(
											paying.reduce(
												(t, id) => t + Number(invoiceById.get(id)?.total ?? 0) / 100,
												0
											)
									  )}`}
							</button>
						</div>
					</div>
				</div>
			)}

			<PaymentDrawer
				open={payOpen}
				onClose={closeDrawer}
				adding={addCardModal.isOpen}
				addForm={<AddPaymentMethod />}
				cards={cards}
				banks={banks}
				wire={wire}
				defaultKind={defaultMethod}
				defaultCardId={defaultCard?.id}
				canManage={canWrite}
				onAddCard={openAddPayment}
				onLinkBank={openAddPayment}
				onSetDefault={setDefault}
				defaultBankId={defaultBankId}
				defaultAccountId={defaultAccountId}
				defaultIsExpired={cardExpired && defaultMethod === 'card'}
				pendingDefault={pendingDefault}
				onRemove={removeCard}
				billingAddress={billingAddress}
				onEditAddress={<UpdateBiilingAddress />}
			/>

			{/* Switching the add-on on or off both accrue to the monthly invoice
			    rather than charging anything now, but each still sits behind an
			    explicit confirmation — turning it off is a real behaviour change
			    (vaults stop being usable at cycle end) and turning it on commits
			    to a recurring line. */}
			{addon && (
				<Confirm
					open={addonConfirm !== null}
					title={addonConfirm === 'off' ? 'Switch eVault off?' : 'Add eVault to your plan?'}
					body={
						addonConfirm === 'off'
							? /* addon.endsAt is always null while this dialog can still be
							     opened (it's only set AFTER confirming); addon.cycleEnd is
							     the real fallback — the SAME field the eVault drive's own
							     off-dialog reads (addon-toggle.tsx), so the two surfaces
							     never quote different dates for the same cycle. Never fall
							     further to Date.now() — that reads as "stays on until
							     today", a fabricated date. */
							  `eVault stays on until ${
									addon.endsAt ?? addon.cycleEnd
										? fmtDate(addon.endsAt ?? addon.cycleEnd)
										: 'the end of this billing cycle'
							  }. After that, vaults cannot be used until you switch it back on. Nothing is deleted.`
							: `eVault will be added to your plan at $${money(addon.price)}/month, billed with your monthly invoice, plus $${money(addon.transferPrice)} per transferred document.`
					}
					label={addonConfirm === 'off' ? 'Switch off' : 'Add to plan'}
					onConfirm={confirmAddon}
					onClose={() => setAddonConfirm(null)}
					loading={addonBusy}
				/>
			)}

		</div>
	);
};
